import React, { useEffect, useState } from 'react';
import { FaArrowDown, FaArrowUp, FaTrashAlt, FaUpload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { userAppData } from '../MainApp.js';
import useS3FileUpload from '../hooks/useS3FileUpload.js';
import {
    IMAGE_MARKUP_MAX_IMAGES,
    IMAGE_MARKUP_MAX_MARKERS_PER_IMAGE,
    getSourceImageUrl,
    normaliseSourceImage,
} from './imageMarkupUtils.js';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };

/** Configure and securely upload the reusable source images for one markup question. */
function StoredSourcePreview({ formId, image }) {
    const [url, setUrl] = useState('');
    useEffect(() => {
        if (!formId || !image?.fileId || image?.previewUrl) return;
        let active = true;
        getSourceImageUrl(formId, image.fileId)
            .then((signedUrl) => { if (active) setUrl(signedUrl); })
            .catch(() => { if (active) setUrl(''); });
        return () => { active = false; };
    }, [formId, image?.fileId, image?.previewUrl]);
    const previewUrl = image?.previewUrl || url;
    return previewUrl
        ? <img src={previewUrl} alt="" />
        : <div className="forms-image-builder-placeholder">Scanning</div>;
}

export default function ImageMarkupBuilder({ question, formId, onChange, disabled }) {
    const images = question?.imageMarkupSettings?.images || [];
    const [uploading, setUploading] = useState(false);
    const { uploadFiles } = useS3FileUpload('formsBuilder');

    function updateImages(nextImages) {
        onChange({
            imageMarkupSettings: {
                selectionMode: 'multiple',
                markersPerImage: 'configurable',
                maxImages: IMAGE_MARKUP_MAX_IMAGES,
                storedFormat: 'jpg',
                maxLongestEdgePixels: 2500,
                jpegQuality: 0.85,
                images: nextImages,
            },
        });
    }

    async function addImages(event) {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = '';
        if (!selectedFiles.length) return;
        if (images.length + selectedFiles.length > IMAGE_MARKUP_MAX_IMAGES) {
            toast.error(`❌ Add no more than ${IMAGE_MARKUP_MAX_IMAGES} source images to this question.`, TOAST_CONFIG);
            return;
        }
        setUploading(true);
        try {
            const additions = [];
            for (const file of selectedFiles) {
                const imageId = window.crypto?.randomUUID?.() || `image-${Date.now()}-${Math.random().toString(16).slice(2)}`;
                const normalisedFile = await normaliseSourceImage(file, imageId);
                const [uploaded] = await uploadFiles(
                    [normalisedFile],
                    userAppData.cogId,
                    { uploadGroupId: question.questionId, questionId: question.questionId }
                );
                additions.push({
                    imageId,
                    name: file.name.replace(/\.[^.]+$/, '').slice(0, 100) || `Location ${images.length + additions.length + 1}`,
                    fileId: uploaded.fileId,
                    originalName: uploaded.oldName,
                    size: uploaded.size,
                    maxMarkers: 1,
                    previewUrl: normalisedFile.previewUrl,
                });
            }
            updateImages([...images, ...additions]);
            toast.success('✅ Location image uploaded for security scanning.', TOAST_CONFIG);
        } catch (error) {
            console.error('Failed to upload Forms builder image', error);
            toast.error(`❌ ${error?.message || 'The location image could not be uploaded.'}`, TOAST_CONFIG);
        } finally {
            setUploading(false);
        }
    }

    function moveImage(index, direction) {
        const target = index + direction;
        if (target < 0 || target >= images.length) return;
        const reordered = [...images];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        updateImages(reordered);
    }

    return <div className="forms-image-builder forms-full-width">
        <div className="forms-image-builder-header">
            <div><strong>Location images</strong><p>JPG and PNG selections are resized and stored as JPEG. Respondents can select and mark more than one image.</p></div>
            <label className="forms-secondary-button forms-image-upload-button"><FaUpload /> {uploading ? 'Uploading...' : 'Add images'}<input type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png" multiple hidden disabled={disabled || uploading || images.length >= IMAGE_MARKUP_MAX_IMAGES} onChange={addImages} /></label>
        </div>
        {images.length === 0 && <p className="forms-guidance">Add at least one image, for example Ground floor, First floor, or Building exterior.</p>}
        {images.map((image, index) => <div className="forms-image-builder-row" key={image.imageId}>
            <StoredSourcePreview formId={formId} image={image} />
            <div className="forms-image-builder-fields">
                <label className="forms-field">Image name<input value={image.name} maxLength={100} disabled={disabled} onChange={(event) => updateImages(images.map((item) => item.imageId === image.imageId ? { ...item, name: event.target.value } : item))} /></label>
                <label className="forms-field forms-image-marker-limit">Maximum markers
                    <input
                        type="number"
                        min="1"
                        max={IMAGE_MARKUP_MAX_MARKERS_PER_IMAGE}
                        value={Number(image.maxMarkers) || 1}
                        disabled={disabled}
                        onChange={(event) => {
                            const nextLimit = Math.max(1, Math.min(IMAGE_MARKUP_MAX_MARKERS_PER_IMAGE, Number(event.target.value) || 1));
                            updateImages(images.map((item) => item.imageId === image.imageId ? { ...item, maxMarkers: nextLimit } : item));
                        }}
                    />
                </label>
            </div>
            <div className="forms-image-builder-actions">
                <button type="button" onClick={() => moveImage(index, -1)} disabled={disabled || index === 0} aria-label="Move image up"><FaArrowUp /></button>
                <button type="button" onClick={() => moveImage(index, 1)} disabled={disabled || index === images.length - 1} aria-label="Move image down"><FaArrowDown /></button>
                <button type="button" onClick={() => updateImages(images.filter((item) => item.imageId !== image.imageId))} disabled={disabled} aria-label="Remove image"><FaTrashAlt /></button>
            </div>
        </div>)}
        <p className="forms-guidance">Maximum 10 images and {IMAGE_MARKUP_MAX_MARKERS_PER_IMAGE} markers per image. Source files are limited to 10 MB before conversion and 2500 pixels on the longest edge after conversion.</p>
    </div>;
}
